/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import {
  ArrowUpRightIcon,
  BeakerIcon,
  BriefcaseIcon,
  CalendarIcon,
  ChevronDownIcon,
  DollarSignIcon,
  FileIcon,
  FilterXIcon,
  LinkIcon,
  UserIcon,
} from "lucide-react";

import {
  EmptyDataDash,
  EmptyState,
  PlacementStatusBadge,
  Row,
  TableRoleCell,
  TableTooltipParagraph,
  TrackerTableHeaderContainer,
} from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { placementStatusToLabel } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  DateFilterOption,
  PlacementQueryParams,
  PlacementTrackerResponse,
  PlacementStatus,
  PlacementTrackerGroupBy,
} from "@/types";

type GroupedPlacementKey = PlacementStatus | number | "unassigned";

export const PlacementTrackerTable = ({
  groupBy,
  handleUpdateView,
  placementTrackerQueryParams,
  placements,
}: {
  groupBy?: PlacementTrackerGroupBy;
  handleUpdateView: (placementTrackerQueryParams: PlacementQueryParams) => void;
  placementTrackerQueryParams: PlacementQueryParams;
  placements: PlacementTrackerResponse[];
}) => {
  const { users } = useTaggableUsers();

  const getAccordionTitle = (key: string) => {
    if (groupBy === PlacementTrackerGroupBy.Owner) {
      const owner = users.find((user) => user.id.toString() === key);
      return owner ? `${owner.firstName} ${owner.lastName}` : "Unknown";
    } else if (groupBy === PlacementTrackerGroupBy.Status) {
      return placementStatusToLabel[key as PlacementStatus];
    }
    return key;
  };

  // Generic way to group by Placement field selected by the staffer
  // Establish a dictionary of key (either placement status or owner id) to array of placements that have that status or owned by that person.
  // TODO STF-155: Cleanup this logic to make it like the JobTrackerTable.tsx groupBy logic.
  let groupedPlacements;
  if (groupBy) {
    groupedPlacements = placements.reduce<
      Partial<Record<GroupedPlacementKey, PlacementTrackerResponse[]>>
    >((acc, placement) => {
      let key: GroupedPlacementKey;
      if (groupBy === PlacementTrackerGroupBy.Owner) {
        // TODO: Make this more robust by retrieving owner ID from the BE.
        // Match owner by name to find the user ID
        const owner = users.find(
          (user) =>
            `${user.firstName} ${user.lastName}` === placement.ownerName,
        );
        key = owner ? owner.id : "unassigned";
      } else {
        key = placement.status;
      }
      acc[key] = [...(acc[key] || []), placement];
      return acc;
    }, {});
  }

  return groupedPlacements && placements.length > 0 ? (
    <>
      {Object.entries(groupedPlacements).map(([key, placements]) => (
        <div
          key={key}
          className="mt-4 overflow-hidden rounded-lg border shadow-sm first:mt-0"
        >
          <Collapsible className="group/collapsible" defaultOpen>
            <CollapsibleTrigger asChild>
              <Button
                className="flex w-full justify-between rounded-b-none bg-primary-300 p-3 hover:bg-primary-400"
                onClick={() => {
                  mixpanelUtils.trackButtonClick(
                    "Group header toggle",
                    MIXPANEL_SCREENS.PLACEMENT_TRACKER,
                    { group: getAccordionTitle(key) },
                  );
                }}
              >
                <Row className="items-center gap-2 text-slate-700">
                  <ChevronDownIcon className="transition-transform group-data-[state=open]/collapsible:rotate-180" />
                  <span>{getAccordionTitle(key)}</span>
                </Row>
                <Row className="items-center gap-2">
                  <Badge variant="secondary">
                    {placements?.length ?? 0}{" "}
                    {placements?.length === 1 ? "placement" : "placements"}
                  </Badge>
                  {/* Remove offered/accepted placements from revenue calculation for now, future TODO is to include rejected and pending revenue in tracker as well */}
                  <Badge variant="secondary">
                    $
                    {(placements || [])
                      .reduce(
                        (sum, p) =>
                          sum +
                          (p.revenueEarned &&
                          ![
                            PlacementStatus.OFFERED,
                            PlacementStatus.ACCEPTED,
                          ].includes(p.status)
                            ? p.revenueEarned
                            : 0),
                        0,
                      )
                      .toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}{" "}
                    revenue
                  </Badge>
                </Row>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <BasePlacementTrackerTable
                key={key}
                handleClearAllFilters={handleUpdateView}
                placementTrackerQueryParams={placementTrackerQueryParams}
                placements={placements || []}
                partOfGroup
              />
            </CollapsibleContent>
          </Collapsible>
        </div>
      ))}
    </>
  ) : (
    <BasePlacementTrackerTable
      handleClearAllFilters={handleUpdateView}
      placementTrackerQueryParams={placementTrackerQueryParams}
      placements={placements}
    />
  );
};

const BasePlacementTrackerTable = ({
  handleClearAllFilters,
  placementTrackerQueryParams,
  placements,
  partOfGroup,
}: {
  handleClearAllFilters: (
    placementTrackerQueryParams: PlacementQueryParams,
  ) => void;
  placementTrackerQueryParams: PlacementQueryParams;
  placements: PlacementTrackerResponse[];
  partOfGroup?: boolean;
}) => {
  return (
    <div
      className={cn(
        "relative overflow-auto",
        partOfGroup ? "max-h-[60vh]" : "max-h-[78vh] rounded-lg border",
      )}
    >
      <table className="w-full table-fixed text-sm">
        <TableHeader className="sticky top-0 bg-slate-50 [&_tr]:border-b-0">
          <TableRow className="sticky-border-bottom">
            <TableHead className="w-[24%]">
              <TrackerTableHeaderContainer Icon={BriefcaseIcon} label="Role" />
            </TableHead>
            <TableHead className="w-[15%]">
              <TrackerTableHeaderContainer Icon={UserIcon} label="Owner" />
            </TableHead>
            <TableHead className="w-[10%]">
              <TrackerTableHeaderContainer Icon={BeakerIcon} label="Status" />
            </TableHead>
            <TableHead className="w-[15%]">
              <TrackerTableHeaderContainer Icon={UserIcon} label="Candidate" />
            </TableHead>
            <TableHead className="w-[10%]">
              <TrackerTableHeaderContainer
                Icon={CalendarIcon}
                label="Date Accepted"
              />
            </TableHead>
            <TableHead className="w-[10%]">
              <TrackerTableHeaderContainer
                Icon={CalendarIcon}
                label="Date Offered"
              />
            </TableHead>
            <TableHead className="w-[10%]">
              <TrackerTableHeaderContainer
                Icon={DollarSignIcon}
                label="Revenue"
              />
            </TableHead>
            <TableHead className="w-[6%]">
              <TrackerTableHeaderContainer Icon={LinkIcon} label="Link" />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {placements.length > 0 ? (
            placements.map((placement) => {
              return (
                <TableRow key={placement.id} className="cursor-default">
                  <TableRoleCell
                    companyName={placement.company.name}
                    jobCity={placement.job.city}
                    jobPausedAt={placement.job.pausedAt}
                    jobPauseReason={placement.job.pauseReason}
                    jobPauseType={placement.job.pauseType}
                    jobState={placement.job.state}
                    jobStatus={placement.job.status}
                    jobTitle={placement.job.title}
                  />
                  <TableCell>
                    <Badge variant="secondary"> {placement.ownerName}</Badge>
                  </TableCell>
                  <TableCell>
                    <PlacementStatusBadge status={placement.status} />
                  </TableCell>
                  <TableCell>
                    <TableTooltipParagraph text={placement.candidacy.name} />
                  </TableCell>
                  <TableCell>
                    {placement.dateAccepted ? (
                      <Badge
                        variant="secondary"
                        className="gap-2 border-green-200 bg-green-50 text-green-700 hover:border-green-200 hover:bg-green-50"
                      >
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {dates.formatIsoDateForUser(placement.dateAccepted)}
                      </Badge>
                    ) : (
                      <EmptyDataDash />
                    )}
                  </TableCell>
                  <TableCell>
                    {placement.dateOffered ? (
                      <Badge
                        variant="secondary"
                        className="gap-2 border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-200 hover:bg-slate-50"
                      >
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {dates.formatIsoDateForUser(placement.dateOffered)}
                      </Badge>
                    ) : (
                      <EmptyDataDash />
                    )}
                  </TableCell>
                  <TableCell>
                    {placement.revenueEarned !== null ? (
                      `$${placement.revenueEarned.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}`
                    ) : (
                      <EmptyDataDash />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(
                          `/companies/${placement.company.id}/jobs/${placement.job.id}?c=${placement.candidacy.id}&t=placements`,
                          "_blank",
                        );
                      }}
                      variant="ghost"
                    >
                      <ArrowUpRightIcon />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell className="p-0" colSpan={8}>
                <EmptyState
                  className="bg-slate-50 py-14"
                  description={`No placements were found. Try clearing some filters to see placements!`}
                  Icon={FileIcon}
                  primaryButtonName="Clear All Filters"
                  PrimaryButtonIcon={FilterXIcon}
                  handlePrimaryClick={() => {
                    handleClearAllFilters({
                      ...placementTrackerQueryParams,
                      dateAccepted: undefined,
                      dateAcceptedFilter: DateFilterOption.AllTime,
                      ownerIds: [],
                      placementStatuses: [],
                    });
                  }}
                  title="No placements found"
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </table>
    </div>
  );
};
